import React from 'react'
import { Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../../helpers/react-select-styles'
import { DateFormat, CurrencyFormat, PercentFormat } from '../../../../helpers/formatting'
import { useSelector } from 'react-redux'
import { selectDropdownOptions } from '../../../../redux/dropdownSlice'
import Instruction from '../../../../components/elements/Instruction'

export default function CashOutRefinanceFields({
  control,
  refinanceInfo,
  newLoanAmount,
  payFrequencyOptions,
  dayOfWeekOptions,
  weekOfMonthOptions,
  dayOfMonthOptions,
  availablePrograms,
  payFrequencyValue,
  payFrequencyValueOption,
  setPayFrequencyValue,
  setPayFrequencyValueOption,
  handleProgramChange,
  minPrincipal,
  maxPrincipal,
  minApr,
  maxApr,
  numOfInstallments,
  nextDay,
  watch,
}) {
  const dropdownOptions = useSelector(selectDropdownOptions);
  const terms = refinanceInfo?.terms || {};
  const minCashout = parseFloat(terms.min_additional_principal_amount || refinanceInfo?.min_additional_funds || 0);
  const maxCashout = parseFloat(terms.max_additional_principal_amount || refinanceInfo?.max_additional_funds || 0);

  if (refinanceInfo && refinanceInfo?.lead_id == null) {
    return (
      <Instruction content={"Something went wrong. Please return to the payment schedule screen and click on the Refinance - Cash Out quick action."} />
    )
  }

  return (
    <>

      {/* Old loan's payoff amount */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="email_address">Old loan&apos;s payoff amount:</label>
        <div className="control"><CurrencyFormat value={refinanceInfo?.payoff_amount} /></div>
      </div>

      {/* Additional Funds */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="additional_funds">Additional Funds:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="additional_funds"
            control={control}
            defaultValue={minCashout}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                className="input is-small"
                min={minCashout}
                max={maxCashout}
                step="0.01"
                onChange={(e) => {
                  // Allow any value while typing
                  field.onChange(e.target.value);
                }}
              />
            )}
          />
          <div><small className={watch('additional_funds') && (watch('additional_funds') < minCashout || watch('additional_funds') > maxCashout) ? 'has-text-danger' : ''}>
            Min: <CurrencyFormat value={minCashout} /> - Max: <CurrencyFormat value={maxCashout} />
          </small></div>
        </div>
      </div>

      {/* New Loan Amount */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`}>New Loan Amount:</label>
        <div className="control"><CurrencyFormat value={newLoanAmount} /></div>
      </div>

      {/* New Loan Effective Date */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`}>New Loan Effective Date:</label>
        <div className="control"><DateFormat dateString={refinanceInfo?.effective_date} /></div>
      </div>

      {/* Funding Method */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="funding_method">Funding Method:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          {(() => {
            const fundingMethodName = dropdownOptions.filter(
              (o) => o.code_key === refinanceInfo?.loan_funding_method
            )[0]?.name || refinanceInfo?.loan_funding_method || '';
            return <span>{fundingMethodName}</span>;
          })()}

        </div>
      </div>

      {/* Pay Frequency */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="pay_frequency_dropdown_option_id">Pay Frequency:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="pay_frequency_dropdown_option_id"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                {...field}
                classNames={reactSelectClassNames()}
                options={payFrequencyOptions}
                isSearchable={false}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  menu: ({ ...css }) => ({ ...css, width: '100%' }),
                }}
                menuPosition="fixed"
                onChange={(val) => {
                  setPayFrequencyValue(val?.label);
                  setPayFrequencyValueOption("");
                  // Reset all dependent fields
                  if (typeof control?.setValue === 'function') {
                    control.setValue('semi_monthly_pay_frequency', '');
                    control.setValue('monthly_pay_frequency', '');
                    control.setValue('pay_first_day_of_month_dropdown_option_id', '');
                    control.setValue('pay_second_day_of_month_dropdown_option_id', '');
                    control.setValue('pay_first_week_of_month_dropdown_option_id', '');
                    control.setValue('pay_second_week_of_month_dropdown_option_id', '');
                    control.setValue('pay_first_day_of_week_dropdown_option_id', '');
                    control.setValue('pay_second_day_of_week_dropdown_option_id', '');
                    control.setValue('next_pay_date', '');
                  }
                  field.onChange({ label: val?.label, value: val?.value });
                }}
              />
            )}
          />
        </div>
      </div>

      {/* Pay frequency dependent fields - always show if payFrequencyValue is selected */}
      {payFrequencyValue && (
        <>
          {/* Semi-Monthly Option */}
          {payFrequencyValue === 'Semi-Monthly' && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="semi_monthly_pay_frequency">Semi Monthly Option:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="semi_monthly_pay_frequency"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={[{
                        label: 'Specific Days',
                        value: 'semimonthly-option1',
                      }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }]}
                      isSearchable={false}
                      onChange={(val) => {
                        field.onChange({ label: val?.label, value: val?.value })
                        setPayFrequencyValueOption(val?.value)
                      }}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Monthly Option */}
          {payFrequencyValue === 'Monthly' && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="monthly_pay_frequency">Monthly Option:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="monthly_pay_frequency"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={[{
                        label: 'Pay Day of Month',
                        value: 'monthly-option1',
                      }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }]}
                      isSearchable={false}
                      onChange={(val) => {
                        field.onChange({ label: val?.label, value: val?.value })
                        setPayFrequencyValueOption(val?.value)
                      }}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* First Day of Week */}
          {(payFrequencyValue === 'Weekly' || (payFrequencyValue === 'Semi-Monthly' && payFrequencyValueOption === 'semimonthly-option2') || (payFrequencyValue === 'Monthly' && payFrequencyValueOption === 'monthly-option2')) && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="pay_first_day_of_week_dropdown_option_id">First Day of Week:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="pay_first_day_of_week_dropdown_option_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={dayOfWeekOptions}
                      isSearchable={false}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* First Payment (Bi-Weekly) */}
          {/* This field must always stay mounted so React Hook Form can keep it registered. We only hide it visually using "is-hidden". If we conditionally unmount this input, React Hook Form will lose the field on first render and the submitted value will be null on first submit. Do not wrap this in a conditional render unless you handle registration manually. */}
          <div className={`field is-horizontal ${payFrequencyValue === 'Bi-Weekly' ? '' : 'is-hidden'}`}>
            <label className={`refinance-loan-modal-label`} htmlFor="next_pay_date">Next Payment Day:<span className="has-text-danger">*</span></label>
            <div className="control refinance-loan-modal-fields">
              <Controller
                name="next_pay_date"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    className="input is-small"
                    min={nextDay}
                  />
                )}
              />
            </div>
          </div>

          {/* Second Day of Week */}
          {payFrequencyValue === 'Semi-Monthly' && payFrequencyValueOption === 'semimonthly-option2' && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="pay_second_day_of_week_dropdown_option_id">Second Day of Week:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="pay_second_day_of_week_dropdown_option_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={dayOfWeekOptions}
                      isSearchable={false}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* First Week of Month */}
          {((payFrequencyValue === 'Semi-Monthly' && payFrequencyValueOption === 'semimonthly-option2') || (payFrequencyValue === 'Monthly' && payFrequencyValueOption === 'monthly-option2')) && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="pay_first_week_of_month_dropdown_option_id">First Week of Month:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="pay_first_week_of_month_dropdown_option_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={weekOfMonthOptions}
                      isSearchable={false}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Second Week of Month */}
          {payFrequencyValue === 'Semi-Monthly' && payFrequencyValueOption === 'semimonthly-option2' && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="pay_second_week_of_month_dropdown_option_id">Second Week of Month:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="pay_second_week_of_month_dropdown_option_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={weekOfMonthOptions}
                      isSearchable={false}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* First Day of Month */}
          {((payFrequencyValue === 'Semi-Monthly' && payFrequencyValueOption === 'semimonthly-option1') || (payFrequencyValue === 'Monthly' && payFrequencyValueOption === 'monthly-option1')) && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="pay_first_day_of_month_dropdown_option_id">First Day of Month:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="pay_first_day_of_month_dropdown_option_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={dayOfMonthOptions}
                      isSearchable={false}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}

          {/* Second Day of Month */}
          {payFrequencyValue === 'Semi-Monthly' && payFrequencyValueOption === 'semimonthly-option1' && (
            <div className="field is-horizontal">
              <label className={`refinance-loan-modal-label`} htmlFor="pay_second_day_of_month_dropdown_option_id">Second Day of Month:<span className="has-text-danger">*</span></label>
              <div className="control refinance-loan-modal-fields">
                <Controller
                  name="pay_second_day_of_month_dropdown_option_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={dayOfMonthOptions}
                      isSearchable={false}
                      onChange={(val) => field.onChange({ label: val?.label, value: val?.value })}
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        menu: ({ ...css }) => ({ ...css, width: '100%' }),
                      }}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Holiday or Weekend Payday */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="first_name">Holiday or Weekend Payday:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="is_pay_before_holiday"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                {...field}
                classNames={reactSelectClassNames()}
                options={[{ label: 'Before', value: 1 }, { label: 'After', value: 0 }]}
                isSearchable={false}
                onChange={(val) => {
                  field.onChange({ label: val?.label, value: val?.value })
                }}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  menu: ({ ...css }) => ({ ...css, width: '100%' }),
                }}
                menuPosition="fixed"
              />
            )}
          />
        </div>
      </div>

      {/* Program */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="first_name">Program:<span className="has-text-danger">*</span></label>
        <div className="field">
          <div className="control refinance-loan-modal-fields">
            <div style={{ width: '100%' }}>
              <Controller
                name="program_id"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    classNames={reactSelectClassNames()}
                    options={availablePrograms?.map((o) => ({
                      label: o.name,
                      value: o.id,
                      pay_frequency_dropdown_option_id: o.pay_frequency_dropdown_option_id,
                    }))}
                    isSearchable={false}
                    onChange={(val) => {
                      handleProgramChange(field, val)
                    }}
                    styles={{
                      container: (base) => ({ ...base, width: '100%' }),
                      menu: ({ ...css }) => ({ ...css, width: '100%' }),
                    }}
                    menuPosition="fixed"
                  />
                )}
              />
            </div>
          </div>
          {minPrincipal && maxPrincipal && (
            <p className="help">{`Min Principal: $${minPrincipal} - Max Principal: $${maxPrincipal}`}</p>
          )}
          {minApr && maxApr && (
            <p className="help">Min APR: <PercentFormat value={minApr} fractionDigits={3} />{" - "}Max APR: <PercentFormat value={maxApr} fractionDigits={3} /></p>
          )}
          {numOfInstallments &&
            <p className="help">{`Number Of Installments: ${numOfInstallments}`}</p>}
        </div>
      </div>

      {/* APR */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="first_name">APR:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="apr"
            control={control}
            defaultValue={(() => {
              const apr = refinanceInfo?.active_loan?.apr;
              if (apr !== undefined && apr !== null && !Number.isNaN(apr)) {
                if (minApr !== null && apr < minApr) return minApr;
                if (maxApr !== null && apr > maxApr) return maxApr;
                return apr;
              }
              return minApr || '';
            })()}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                className="input is-small"
                min={minApr || undefined}
                max={maxApr || undefined}
                step="0.001"
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
              />
            )}
          />
          <div><small className={watch('apr') && ((minApr !== null && watch('apr') < minApr) || (maxApr !== null && watch('apr') > maxApr)) ? 'has-text-danger' : ''}>
            Min: <PercentFormat value={minApr} fractionDigits={3} />{" - "}Max: <PercentFormat value={maxApr} fractionDigits={3} />
          </small></div>
        </div>
      </div>

    </>
  )
}
